/**
 * Shared HTML print-view helpers (v2, FR-C2~C4 / FR-E1).
 * Pure functions: data in → self-contained printable HTML string out.
 * The browser's print dialog produces the PDF (OD-v2-2) — no server, no deps.
 */

import { DISCLAIMER_KO } from "@/lib/merge";

export function esc(v: unknown): string {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** "—" for empty values so 미기재 is visible on paper. */
export function dash(v: unknown): string {
  const s = String(v ?? "").trim();
  return s ? esc(s) : "—";
}

const PRINT_CSS = `
  * { box-sizing: border-box; }
  body { font-family: 'Malgun Gothic', 'Apple SD Gothic Neo', sans-serif;
         margin: 24px auto; max-width: 800px; color: #1a202c; font-size: 13px; }
  h1 { font-size: 20px; border-bottom: 2px solid #1a202c; padding-bottom: 8px; }
  h2 { font-size: 14px; margin: 18px 0 6px; }
  table { width: 100%; border-collapse: collapse; margin: 6px 0 12px; }
  th, td { border: 1px solid #cbd5e0; padding: 5px 8px; text-align: left; vertical-align: top; }
  th { background: #f7fafc; white-space: nowrap; width: 180px; font-weight: 600; }
  table.cols th { width: auto; }
  .num { text-align: right; }
  .badge { display: inline-block; padding: 1px 8px; border-radius: 999px; font-size: 11px;
           border: 1px solid #cbd5e0; margin-right: 4px; }
  .badge.warn { background: #fffaf0; border-color: #dd6b20; color: #9c4221; }
  .badge.ok { background: #f0fff4; border-color: #38a169; color: #276749; }
  .badge.block { background: #fff5f5; border-color: #e53e3e; color: #9b2c2c; }
  .note { background: #ebf8ff; border: 1px solid #90cdf4; border-radius: 6px; padding: 8px 12px; margin: 8px 0; }
  .warnbox { background: #fffaf0; border: 1px solid #f6ad55; border-radius: 6px; padding: 8px 12px; margin: 8px 0; }
  .disclaimer { margin-top: 24px; padding: 10px 12px; border: 1px dashed #a0aec0;
                font-size: 11px; color: #4a5568; white-space: pre-wrap; }
  .meta { color: #718096; font-size: 11px; margin-top: 4px; }
  ul.check { list-style: none; padding-left: 0; }
  ul.check li { margin: 3px 0; }
  ul.check li::before { content: "☐ "; }
  ul.check li.done::before { content: "☑ "; }
  @media print { body { margin: 0; max-width: none; } .no-print { display: none; } }
`;

/** Wrap body HTML into a standalone printable page. */
export function printPage(title: string, bodyHtml: string): string {
  return `<!doctype html><html lang="ko"><head><meta charset="utf-8">
<title>${esc(title)}</title><style>${PRINT_CSS}</style></head><body>
<p class="no-print" style="text-align:right"><button onclick="window.print()">인쇄 / PDF 저장</button></p>
${bodyHtml}
<div class="disclaimer">${esc(DISCLAIMER_KO)}</div>
</body></html>`;
}

/** key-value rows table. */
export function kvTable(rows: [string, unknown][]): string {
  return `<table>${rows
    .map(([k, v]) => `<tr><th>${esc(k)}</th><td>${dash(v)}</td></tr>`)
    .join("")}</table>`;
}
